import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextInput,
} from '@c2fo/liquidity';
import { CompanyStatusDocument } from '@/enterprise/data/activationStatus';
import useCreateSupplierCompany from '@/enterprise/data/useCreateSupplierCompany';
import useInstantPayTerms from '@/enterprise/data/useInstantPayTerms';
import useMarkTermsAsAcknowledged from '@/enterprise/data/useMarkTermsAsAcknowledged';
import { CompanyDocumentType } from '@/enterprise/data/useUploadCompanyDocument';
import { useReporting } from '@/reporting';
import useLocaleFormat from '@/utils/useLocaleFormat';
import CompanyDocumentUpload from './CompanyDocumentUpload';
import { trackInstantPayOnboarding } from './trackInstantPayOnboarding';
import { US_STATES, UsStateCode } from './usStates';

interface BusinessVerificationFormValues {
  agreedToTerms: boolean;
  streetAddress: string;
  city: string;
  // Empty string is the "nothing picked yet" state; `required` below rejects it.
  state: UsStateCode | '';
  postalCode: string;
  tin: string;
}

export interface BusinessAddress {
  streetAddress: string;
  city: string;
  state: string;
  postalCode: string;
}

interface BusinessVerificationCardProps {
  hasSubmittedTin: boolean;
  documents: CompanyStatusDocument[];
  /** The submitted TIN, from company status — null until the company has been created. */
  taxId: string | null;
  /** The submitted address, from company status — null for a company created before it was collected. */
  businessAddress: BusinessAddress | null;
}

const CompleteChip = () => {
  const { t } = useTranslation();
  return <Chip label={t('instantPay.onboarding.terms.complete')} variant="outlined" isRound size="xs" type="primary" />;
};

const BusinessVerificationCard = ({
  hasSubmittedTin,
  documents,
  taxId,
  businessAddress,
}: BusinessVerificationCardProps) => {
  const { t } = useTranslation();
  const { asDateStringText } = useLocaleFormat();
  const { trackEnterpriseEvent } = useReporting();
  const { data: instantPayTerms } = useInstantPayTerms();
  const acknowledgeTerms = useMarkTermsAsAcknowledged();
  const createCompany = useCreateSupplierCompany();
  const [isMissingTerms, setIsMissingTerms] = useState(false);

  const taxIdVerificationDocumentUuid =
    documents.find((document) => document.documentType === 'tax_id_verification')?.documentUuid ?? null;
  const articlesOfIncorporationDocumentUuid =
    documents.find((document) => document.documentType === 'articles_of_incorporation')?.documentUuid ?? null;

  const {
    control,
    register,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm<BusinessVerificationFormValues>({
    defaultValues: { agreedToTerms: false, streetAddress: '', city: '', state: '', postalCode: '', tin: '' },
  });

  // RHF focuses the first invalid field in registration order on submit, so these are
  // registered in the same order they appear on screen (the state field uses Controller
  // and can't receive focus-on-error this way).
  const agreedField = register('agreedToTerms', { required: true });

  const streetAddressField = register('streetAddress', {
    required: t('instantPay.onboarding.address.streetAddressError'),
    setValueAs: (value: string) => value.trim(),
  });
  const cityField = register('city', {
    required: t('instantPay.onboarding.address.cityError'),
    setValueAs: (value: string) => value.trim(),
  });
  const postalCodeField = register('postalCode', {
    required: t('instantPay.onboarding.address.postalCodeError'),
    pattern: { value: /^\d{5}(-\d{4})?$/, message: t('instantPay.onboarding.address.postalCodeError') },
    setValueAs: (value: string) => value.trim(),
  });
  const tinField = register('tin', {
    required: t('instantPay.onboarding.terms.tinError'),
    pattern: { value: /^\d{2}-\d{7}$/, message: t('instantPay.onboarding.terms.tinError') },
  });

  const formatTin = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 9);
    return digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
  };

  const onSubmit = handleSubmit(({ streetAddress, city, state, postalCode, tin }) => {
    // `state` is validated as required above, so it can't be the empty placeholder here.
    if (state === '') {
      return;
    }

    acknowledgeTerms.reset();
    createCompany.reset();
    setIsMissingTerms(!instantPayTerms);

    // SEA's create-company endpoint doesn't record or require terms acceptance, so it's recorded first.
    if (!instantPayTerms) {
      return;
    }

    acknowledgeTerms.mutate(instantPayTerms.uuid, {
      onSuccess: () =>
        createCompany.mutate(
          { taxId: tin, streetAddress, city, state, postalCode },
          { onSuccess: () => trackInstantPayOnboarding(trackEnterpriseEvent, { step: 1, action: 'started' }) }
        ),
    });
  });

  const handleDocumentUploaded = (documentType: CompanyDocumentType) => {
    const otherDocumentUuid =
      documentType === 'tax_id_verification' ? articlesOfIncorporationDocumentUuid : taxIdVerificationDocumentUuid;

    trackInstantPayOnboarding(trackEnterpriseEvent, {
      step: 1,
      action: otherDocumentUuid ? 'document-completed' : 'document-started',
      documentType,
    });
  };

  const isSubmitting = acknowledgeTerms.isPending || createCompany.isPending;
  const hasSubmitError = isMissingTerms || acknowledgeTerms.isError || createCompany.isError;
  const isTinAndTermsComplete = hasSubmittedTin;

  return (
    <div className="flex flex-col gap-4">
      {isTinAndTermsComplete ? (
        <div className="flex flex-col gap-6 rounded-xl bg-canvas p-4">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-primary">{t('instantPay.onboarding.terms.title')}</span>
              <CompleteChip />
            </div>
            {instantPayTerms?.acknowledgedAt && (
              <span className="text-sm text-text-secondary">
                {t('instantPay.onboarding.terms.signedOn', {
                  date: asDateStringText({ date: instantPayTerms.acknowledgedAt, formatOptions: { year: 'numeric' } }),
                })}
              </span>
            )}
          </div>
          {businessAddress && (
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className="font-medium text-text-primary">
                  {t('instantPay.onboarding.address.completeLabel')}
                </span>
                <CompleteChip />
              </div>
              <span className="text-sm text-text-secondary">{businessAddress.streetAddress}</span>
              <span className="text-sm text-text-secondary">{`${businessAddress.city}, ${businessAddress.state}`}</span>
              <span className="text-sm text-text-secondary">{businessAddress.postalCode}</span>
            </div>
          )}
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-primary">{t('instantPay.onboarding.terms.tinLabel')}</span>
              <CompleteChip />
            </div>
            {taxId && <span className="text-sm text-text-secondary">{taxId}</span>}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div
            className={`flex flex-col gap-2 rounded-xl border bg-canvas p-4 ${
              errors.agreedToTerms ? 'border-red-600' : 'border-transparent'
            }`}
          >
            <span className="font-medium text-text-primary">{t('instantPay.onboarding.terms.title')}</span>
            <div className="flex items-center gap-2.5 rounded-xl bg-deepGray-50 px-6 py-4">
              <Checkbox
                id="agreedToTerms"
                className="bg-white"
                {...agreedField}
                onChange={(e) => {
                  agreedField.onChange(e);
                  if (errors.agreedToTerms) {
                    trigger('agreedToTerms');
                  }
                }}
              />
              <Label htmlFor="agreedToTerms" className="font-normal">
                {t('instantPay.onboarding.terms.checkboxLabel')}{' '}
                {instantPayTerms?.contentUrl ? (
                  <a
                    className="font-medium text-blue-500"
                    href={instantPayTerms.contentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('instantPay.onboarding.terms.linkLabel')}
                  </a>
                ) : (
                  t('instantPay.onboarding.terms.linkLabel')
                )}
              </Label>
            </div>
            {errors.agreedToTerms && (
              <p className="text-sm text-red-600">{t('instantPay.onboarding.terms.checkboxError')}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 rounded-xl bg-canvas p-4 lg:grid-cols-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="streetAddress" required>
                {t('instantPay.onboarding.address.streetAddressLabel')}
              </Label>
              <TextInput
                id="streetAddress"
                className="w-full"
                hasError={!!errors.streetAddress}
                {...streetAddressField}
              />
              {errors.streetAddress?.message && <p className="text-sm text-red-600">{errors.streetAddress.message}</p>}
              <span className="text-sm text-text-secondary">
                {t('instantPay.onboarding.address.streetAddressNotice')}
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="city" required>
                {t('instantPay.onboarding.address.cityLabel')}
              </Label>
              <TextInput id="city" className="w-full" hasError={!!errors.city} {...cityField} />
              {errors.city?.message && <p className="text-sm text-red-600">{errors.city.message}</p>}
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="state" required>
                {t('instantPay.onboarding.address.stateLabel')}
              </Label>
              <Controller
                control={control}
                name="state"
                rules={{ required: t('instantPay.onboarding.address.stateError') }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger
                      id="state"
                      aria-label={t('instantPay.onboarding.address.stateLabel')}
                      hasError={!!errors.state}
                    >
                      <SelectValue placeholder={t('instantPay.onboarding.address.statePlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map(({ code, name }) => (
                        <SelectItem key={code} value={code}>
                          {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.state?.message && <p className="text-sm text-red-600">{errors.state.message}</p>}
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="postalCode" required>
                {t('instantPay.onboarding.address.postalCodeLabel')}
              </Label>
              <TextInput id="postalCode" className="w-full" hasError={!!errors.postalCode} {...postalCodeField} />
              {errors.postalCode?.message && <p className="text-sm text-red-600">{errors.postalCode.message}</p>}
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="tin" required>
                {t('instantPay.onboarding.terms.tinLabel')}
              </Label>
              <TextInput
                id="tin"
                className="w-full"
                placeholder={t('instantPay.onboarding.terms.tinPlaceholder')}
                maxLength={10}
                hasError={!!errors.tin}
                {...tinField}
                onChange={(e) => {
                  e.target.value = formatTin(e.target.value);
                  tinField.onChange(e);
                }}
              />
              {errors.tin?.message && <p className="text-sm text-red-600">{errors.tin.message}</p>}
              <span className="text-sm text-text-secondary">{t('instantPay.onboarding.terms.multipleTinNotice')}</span>
            </div>
          </div>

          {hasSubmitError && (
            <Alert full type="error" variant="outlined" description={t('instantPay.onboarding.terms.submitError')} />
          )}

          <Button variant="primary" className="self-end" loading={isSubmitting} onClick={onSubmit}>
            {t('instantPay.onboarding.terms.submitCta')}
          </Button>
        </div>
      )}

      <hr className="border-stroke opacity-38" />

      <div className="flex flex-col gap-4">
        <CompanyDocumentUpload
          documentType="tax_id_verification"
          label={t('instantPay.onboarding.documents.taxId')}
          description={t('instantPay.onboarding.documents.taxIdDescription')}
          documentUuid={taxIdVerificationDocumentUuid}
          disabled={!isTinAndTermsComplete}
          onUploaded={handleDocumentUploaded}
        />
        <CompanyDocumentUpload
          documentType="articles_of_incorporation"
          label={t('instantPay.onboarding.documents.articlesOfIncorporation')}
          description={t('instantPay.onboarding.documents.articlesOfIncorporationDescription')}
          documentUuid={articlesOfIncorporationDocumentUuid}
          disabled={!isTinAndTermsComplete}
          onUploaded={handleDocumentUploaded}
        />
      </div>
    </div>
  );
};

export default BusinessVerificationCard;
